import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { flushSync } from 'react-dom'
import { Routes, useLocation, type Location } from 'react-router-dom'
import BarbaOverlay from '@/components/barba/BarbaOverlay'
import { playEnter, playLeave, resetOverlay } from '@/lib/barba/transitions'
import {
  decideNavigation,
  isSamePath,
  releaseTransitionLock,
  tryAcquireTransitionLock,
} from '@/lib/barba/routeNavigationCoordinator'
import { nextFrame, scrollAfterBarbaNavigation, scrollToLocationHash, shouldAnimateTransition } from '@/lib/barba/utils'

interface BarbaRoutesProps {
  children: ReactNode
}

export default function BarbaRoutes({ children }: BarbaRoutesProps) {
  const location = useLocation()
  const overlayRef = useRef<HTMLDivElement>(null)
  const displayLocationRef = useRef(location)
  const routerLocationRef = useRef(location)
  const isAnimatingRef = useRef(false)
  const pendingLocationRef = useRef<Location | null>(null)
  const mountedRef = useRef(true)
  const [displayLocation, setDisplayLocation] = useState(location)

  useLayoutEffect(() => {
    routerLocationRef.current = location
  }, [location])

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  useEffect(() => {
    displayLocationRef.current = displayLocation
  }, [displayLocation])

  useEffect(() => {
    if (overlayRef.current) {
      resetOverlay(overlayRef.current)
    }
  }, [])

  useEffect(() => {
    const applyDisplayLocation = (target: Location) => {
      displayLocationRef.current = target
      setDisplayLocation(target)
    }

    async function finishScroll(target: Location, fromPathname: string) {
      await nextFrame()
      if (!mountedRef.current) return
      scrollAfterBarbaNavigation(target, fromPathname)
    }

    function drainPending() {
      if (!mountedRef.current) return

      let pending = pendingLocationRef.current
      pendingLocationRef.current = null

      const router = routerLocationRef.current
      const display = displayLocationRef.current
      if (!pending && !isSamePath(router, display)) {
        pending = router
      }

      if (!pending) return
      if (isSamePath(pending, display)) return
      navigateTo(pending)
    }

    function navigateTo(target: Location) {
      const display = displayLocationRef.current
      const decision = decideNavigation(
        target,
        display,
        isAnimatingRef.current,
        shouldAnimateTransition,
      )

      if (decision.type === 'queue') {
        pendingLocationRef.current = target
        return
      }

      if (decision.type === 'noop') {
        return
      }

      if (decision.type === 'instant') {
        const fromPathname = display.pathname
        applyDisplayLocation(decision.target)
        void finishScroll(decision.target, fromPathname)
        return
      }

      if (!tryAcquireTransitionLock(isAnimatingRef)) {
        pendingLocationRef.current = target
        return
      }

      void runTransition(decision.target, display.pathname)
    }

    async function runTransition(target: Location, fromPathname: string) {
      const overlay = overlayRef.current
      if (!overlay) {
        releaseTransitionLock(isAnimatingRef)
        applyDisplayLocation(target)
        void finishScroll(target, fromPathname)
        return
      }

      try {
        await playLeave(overlay)
        if (!mountedRef.current) return

        flushSync(() => {
          applyDisplayLocation(target)
        })
        await nextFrame()
        await nextFrame()
        if (!target.hash) {
          window.scrollTo(0, 0)
        }
        await nextFrame()

        if (!mountedRef.current) return

        await playEnter(overlay)
        if (mountedRef.current) {
          const hashScrolled = scrollToLocationHash(target)
          if (!hashScrolled && target.hash && fromPathname !== target.pathname) {
            window.scrollTo(0, 0)
          }
        }
      } catch {
        if (mountedRef.current) {
          flushSync(() => {
            applyDisplayLocation(target)
          })
          void finishScroll(target, fromPathname)
        }
      } finally {
        releaseTransitionLock(isAnimatingRef)
        if (overlayRef.current) {
          resetOverlay(overlayRef.current)
        }
      }

      drainPending()
    }

    if (isAnimatingRef.current) {
      pendingLocationRef.current = location
      return
    }

    navigateTo(location)
  }, [location])

  return (
    <>
      <div data-barba="wrapper" className="barba-wrapper">
        <div
          data-barba="container"
          data-barba-namespace={displayLocation.pathname}
          className="barba-container"
        >
          <Routes location={displayLocation}>{children}</Routes>
        </div>
      </div>
      <BarbaOverlay ref={overlayRef} />
    </>
  )
}
